/*
* Various kendo grid utilities & helpers
*/

import '@progress/kendo-ui';
import * as _ from 'lodash';
import moment from 'moment';

import AppConfiguration from '../../config/configuration';
import * as AppUtilities from '../apputilities';

export class GridUtilities {
    public static ModuleName: string = 'GridUtilities';

    public static KendoGridPageSizeAll: string = 'all';
    public static KendoGridDefaultPageSize: number = 13;
    public static KendoGridDefaultPageSizes: Array<any> = [5, GridUtilities.KendoGridDefaultPageSize, 25, 50, 100, GridUtilities.KendoGridPageSizeAll];

    public static KendoGridClassTextRight: string = 'k-grid-text-right';
    public static KendoGridClassTextLeft: string = 'k-grid-text-left';
    public static KendoGridClassTextCenter: string = 'k-grid-text-center';
    public static KendoGridClassMultiColumn: string = 'k-grid-multi-column';

    public static KendoGridAddRowClassFieldName: string = 'gridAddRowClass';

    /* Grid column templates */
    public static kendoGridLinkColumnTemplate(linkTextFieldName: string, linkHrefFieldName: string, linkTitleText?: string) {
        // Return a kendo link column template
        if (!linkTextFieldName) { return ''; }
        if (!linkHrefFieldName) { return `#: ${linkTextFieldName} #`; }

        return `<a href="#: ${linkHrefFieldName} #" title="${linkTitleText}">#: ${linkTextFieldName} #</a>`;
    }

    public static kendoGridCheckboxColumnTemplate(booleanFieldName: string) {
        // Return a kendo link column template
        if (!booleanFieldName) { return ''; }

        return `<input className="form-control" type="checkbox" #= ${booleanFieldName} ? "checked=checked" : "" # disabled="disabled />"`;
    }

    public static kendoGridCheckboxColumnTemplateNew(booleanFieldName: string, disabledFieldName: string = 'disabled') {
        // Return a kendo link column template
        if (!booleanFieldName) { return ''; }

        const attrChecked = `#= ${booleanFieldName} ? "checked=checked" : "" #`;
        const attrDisabled = ''; // `#= ${disabledFieldName} ? "disabled="disabled" : "" #`;

        const result = `<input className="form-control" type="checkbox" ${attrChecked} ${attrDisabled} />"`;

        // console.log(`kendoGridCheckboxColumnTemplateNew`, { result, attrChecked, attrDisabled, booleanFieldName, disabledFieldName });

        return result;
    }

    public static kendoGridCheckboxColumnTemplateHandled(booleanFieldName: string, disabledFieldName: string = 'disabled') {
        // Return a kendo link column template
        if (!booleanFieldName) { return ''; }

        const attrChecked = `#= ${booleanFieldName} ? "checked=checked" : "" #`;
        const attrDisabled = ''; // `#= ${disabledFieldName} ? "disabled="disabled" : "" #`;

        const result = `<input className="form-control" type="checkbox" ${attrChecked} ${attrDisabled} />"`;

        return result;
    }

    /* Grid regular column builders */
    public static kendoGridColumnDefinition(valueFieldName: string, headerText: string, width: any, key: number, template?: string, columnOptions?: any, sortByField?: string, attributesClass?: string) {
        // Return kendo grid column options
        let column: any = {
            field: valueFieldName,
            title: headerText,
            width: width,
            key: key,
        };

        if (template) { column.template = template; }
        if (sortByField) { column.sortable = GridUtilities.kendoGridColumnCustomSortDefinition(sortByField); }

        if (attributesClass) {
            column.headerAttributes = { 'class': attributesClass };
            column.attributes = { 'class': attributesClass };
        }

        const result = Object.assign({}, column, columnOptions ? columnOptions : {});

        // console.log(`kendoGridColumnDefinition valueFieldName ${valueFieldName} headerText "${headerText}" key ${key}`, { result, column, columnOptions });

        return result;
    }

    public static kendoGridColumnDefinitionHidden(valueFieldName: string, key: number, headerText: string = '') {
        const width = 50;
        const template = undefined;
        const columnOptions = { hidden: true };

        return GridUtilities.kendoGridColumnDefinition(valueFieldName, headerText, width, key, template, columnOptions);
    }

    public static kendoGridColumnDefinitionTextRight(valueFieldName: string, headerText: string, width: any, key: number, template?: string, columnOptions?: any, sortByField?: string) {
        return GridUtilities.kendoGridColumnDefinition(valueFieldName, headerText, width, key, template, columnOptions, sortByField, GridUtilities.KendoGridClassTextRight);
    }

    public static kendoGridColumnDefinitionTextCenter(valueFieldName: string, headerText: string, width: any, key: number, template?: string, columnOptions?: any, sortByField?: string) {
        return GridUtilities.kendoGridColumnDefinition(valueFieldName, headerText, width, key, template, columnOptions, sortByField, GridUtilities.KendoGridClassTextCenter);
    }

    /* Grid multi column builders */
    public static kendoGridMultiColumnDefinition(headerText: string, key: number, columns: Array<any>, attributesClass: string = '') {
        return GridUtilities.kendoGridColumnDefinition('', headerText, undefined, key, undefined, { columns: columns }, undefined, `${GridUtilities.KendoGridClassMultiColumn} ${attributesClass}`);
    }

    public static kendoGridMultiColumnDefinitionTextCenter(headerText: string, key: number, columns: Array<any>) {
        return GridUtilities.kendoGridMultiColumnDefinition(headerText, key, columns, GridUtilities.KendoGridClassTextCenter);
    }

    /* Grid option builders */
    public static kendoGridOptions(columns: Array<any>, noResultsMessage: string, gridOptions?: any) {
        // Return kendo grid options
        let grid = _.cloneDeep(_.merge(
            {
                sortable: {
                    mode: 'single',
                    allowUnsort: true,
                    showIndexes: true
                },
                resizable: true,
                columns: columns,
                noRecords: {
                    template: noResultsMessage
                }
            },
            gridOptions ? gridOptions : {}
        ));

        return grid;
    }

    public static kendoGridOptionsPaging(columns: Array<any>, noResultsMessage: string, gridOptions: any = {}, exportFilenamePrefix?: string) {
        // Return standard kendo grid options with paging, virtual scrolling and pdf/xls export enabled

        const dateTimeFormatted = moment().format(AppConfiguration.getMomentDateTimeFormat());

        const filenamePrefix = exportFilenamePrefix ? exportFilenamePrefix : 'Grid Data';
        const filename = `${filenamePrefix} ${dateTimeFormatted}`.replace(/ /g, '_');

        let toolbar = _.get(gridOptions, 'toolbar', ['excel', 'pdf']);

        const pageableAlwaysVisible = _.get(gridOptions, 'pageable.alwaysVisible', true);
        const scrollableVirtual = _.get(gridOptions, 'scrollable.virtual', true);

        const result = _.cloneDeep(_.merge(
            {
                sortable: {
                    mode: 'single',
                    allowUnsort: true,
                    showIndexes: true
                },
                resizable: true,
                columns: columns,
                noRecords: {
                    template: noResultsMessage
                },
                filterable: true,
                pageable: {
                    alwaysVisible: pageableAlwaysVisible,
                    pageSizes: GridUtilities.KendoGridDefaultPageSizes,
                    messages: {
                        itemsPerPage: 'per page'
                    }
                },
                scrollable: {
                    virtual: scrollableVirtual
                },
                toolbar: toolbar,
                excel: {
                    allPages: true,
                    fileName: `${filename}.xlsx`,
                },
                pdf: {
                    allPages: true,
                    fileName: `${filename}.pdf`,
                    landscape: true,
                    paperSize: 'auto',
                    margin: {
                        bottom: '5mm',
                        left: '5mm',
                        right: '5mm',
                        top: '1cm',
                    },
                    title: `${filenamePrefix} - ${dateTimeFormatted}`,
                },
            },
            gridOptions ? gridOptions : {}
        ));

        return result;
    }

    public static kendoGridColumnCustomSortDefinition(sortField: string) {
        // Returns a dynamically generated grid column sortable.compare function.
        // This enables sorting on a different column than is rendered
        // More info: https://docs.telerik.com/kendo-ui/api/javascript/ui/grid#configuration-columns.sortable.compare

        return {
            compare: new Function('a', 'b', `return a['${sortField}'] - b['${sortField}'];`)
        };
    }

    public static kendoGridDataSource(records: Array<any>, options: any = {}) {
        // Return Kendo grid DataSource object
        let dataSourceOptions = _.merge(
            {},
            {
                data: AppUtilities.isArrayWithLength(records) ? records : [],
            },
            options ? options : {}
        );

        return new kendo.data.DataSource(dataSourceOptions);
    }

    public static kendoGridDataSourcePaged(records: Array<any>, options: any = {}, pageSize: number = GridUtilities.KendoGridDefaultPageSize) {
        // Return Kendo grid DataSource object, configured for paging if pageSize > 0
        let dataSourceOptions = _.merge(
            {},
            pageSize ? { pageSize: pageSize } : {},
            options ? options : {}
        );

        return GridUtilities.kendoGridDataSource(records, dataSourceOptions);
    }

    public static kendoGridDataSourceServerPaged(
        readFunction: (options: any) => void,
        pageSize: number = GridUtilities.KendoGridDefaultPageSize,
        options: any = {}
    ) {
        // Return Kendo grid DataSource object configured for server-side paging
        // readFunction: function that handles the API call with options parameter
        //   - options.data contains { page, pageSize, skip, take } from Kendo
        //   - Must call options.success({ data: [...], total: count }) on success
        //   - Must call options.error(err) on error
        let dataSourceOptions = _.merge(
            {
                transport: {
                    read: readFunction,
                },
                serverPaging: true,
                pageSize: pageSize,
                schema: {
                    data: 'data',
                    total: 'total',
                },
            },
            options
        );

        return new kendo.data.DataSource(dataSourceOptions);
    }

    /* Grid data helpers */
    public static kendoGridDataboundAddRowClass(e: any, recordAddRowClassField: string = GridUtilities.KendoGridAddRowClassFieldName) {
        // iterate the data items and apply row styles where necessary
        if (!e || !e.sender || !e.sender.dataSource || !recordAddRowClassField) { return; }

        let dataItems = e.sender.dataSource.view();
        _.forEach(dataItems, (dataItem) => {
            const groupItems = _.get(dataItem, 'items', []);
            if (AppUtilities.isArrayWithLength(groupItems)) {
                _.forEach(groupItems, (groupItem) => {
                    const addClass = groupItem.get(recordAddRowClassField);

                    if (addClass) {
                        const row = e.sender.tbody.find(`[data-uid='${groupItem.uid}']`);
                        row.addClass(addClass);
                    }
                });
            } else {
                const addClass = dataItem.get(recordAddRowClassField);

                if (addClass) {
                    const row = e.sender.tbody.find(`[data-uid='${dataItem.uid}']`);
                    row.addClass(addClass);
                }
            }
        });
    }

    public static kendoGridDataboundAutoColumnWidths(e: any) {
        // iterate the data items and apply row styles where necessary
        if (!e || !e.sender || !e.sender.columns) { return; }

        for (let i = 0; i < e.sender.columns.length; i++) {
            e.sender.autoFitColumn(i);
        }
    }

    /* Grid row class styling */
    public static recordSetRowClass(record: any, rowClass: string = '', recordAddRowClassField: string = GridUtilities.KendoGridAddRowClassFieldName) {
        // Set addRowClass field on record
        if (!record || !recordAddRowClassField) { return; }

        record[GridUtilities.KendoGridAddRowClassFieldName] = rowClass;
    }
}



// WEBPACK FOOTER //
// ./src/common/grid/gridutilities.tsx